import React from "react";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import { PriceInfoResponse } from "services/api/catalog/cvh/types";

interface NewPriceResultModalProps {
  result: PriceInfoResponse | null;
  /** Zatvara i rezultat i sam kalkulator: izračun je gotov, čarobnjak nema što dovršiti. */
  onClose: () => void;
}

const formatCurrency = (value: number | null | undefined) => {
  if (value == null) return "-";
  return `${value.toLocaleString("hr-HR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

const formatPercent = (value: number | null | undefined) => {
  if (value == null) return "-";
  return `${value.toLocaleString("hr-HR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
};

const NewPriceResultModal: React.FC<NewPriceResultModalProps> = ({
  result,
  onClose,
}) => {
  return (
    <Modal
      show={!!result}
      position="centered"
      size="medium"
      onHide={onClose}
    >
      <ModalHeader
        title="Rezultat izračuna - Cijena novog vozila"
        onHide={onClose}
      />
      <ModalBody>
        {result && (
          result.calculationError ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded p-4">
                <p className="text-red-800 font-medium">Greška pri izračunu</p>
                <p className="text-red-600 text-sm mt-1">{result.errorMessage}</p>
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-6 overflow-y-auto max-h-[70vh]">
              <div className="bg-blue-50 border border-blue-200 rounded p-4">
                <h3 className="font-medium text-blue-900 mb-3">Cijena novog vozila</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Osnovna cijena (EUR):</span>
                    <span className="font-medium">{formatCurrency(result.basePriceEur)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Osnovna cijena (HRK):</span>
                    <span className="font-medium">{formatCurrency(result.basePrice)}</span>
                  </div>
                  {result.hasCustomsDuty && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Carina ({formatPercent(result.customsDutyPercent)}):</span>
                      <span className="font-medium">{formatCurrency(result.customsDuty)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">PDV ({formatPercent(result.vatRate)}):</span>
                    <span className="font-medium">{formatCurrency(result.vat)}</span>
                  </div>
                  {result.showPPMVComponents ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">PPMV vrijednosna komponenta:</span>
                        <span className="font-medium">{formatCurrency(result.ppmvValueComponent)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">PPMV ekološka komponenta:</span>
                        <span className="font-medium">{formatCurrency(result.ppmvEcologicalComponent)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between">
                      <span className="text-gray-600">PPMV ({formatPercent(result.ppmvPercent)}):</span>
                      <span className="font-medium">{formatCurrency(result.ppmv)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-blue-300 pt-2 mt-2">
                    <span className="text-blue-900 font-medium">Ukupno:</span>
                    <span className="text-blue-900 font-bold">{formatCurrency(result.total)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded p-4">
                <h3 className="font-medium text-slate-900 mb-3">Dodatni podaci</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tečaj:</span>
                    <span className="font-medium">{result.exchangeRate.toLocaleString("hr-HR", { minimumFractionDigits: 6 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Carina se plaća:</span>
                    <span className="font-medium">{result.customsDutyApplied ? "Da" : "Ne"}</span>
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </ModalBody>

      <ModalFooter>
        <div className="flex justify-end px-4 py-3">
          <Button variant="outlined" onClick={onClose}>
            Zatvori
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default NewPriceResultModal;
